// NOTE: the code below would be used in case we use the $def feature of Ajv. Kept in here for future reference
// const elementTypeRef = {
//   $ref: '#/$defs/element',
// };

// TODO: Check if in time we can replace this with the zod schema validation.

import { allChartTypeManifests } from '../features/visual-analytics/charts/factory';
import { TpChartManifest } from '../features/visual-analytics/charts/interfaces';
import { discreteColorSets, numericalColorSets } from '../features/visual-analytics/helpers/colors';
import { CONFIGPARAMTYPES } from '../features/visual-analytics/helpers/configParams';
import { stepDefinitionsList } from '../features/visual-analytics/actions/steps-definitions/stepsFactory';
import { generateFilterSchema } from '../features/visual-analytics/actions/steps-definitions/dbActionStepTypeTableJoin';
import { categoricalOrderValues } from '../features/visual-analytics/helpers/categoricalSorters';
import { dashboardActionTriggerDefs } from '../features/visual-analytics/actions/executeActionTriggers';
import { DASHBOARD_TEMPLATE_ELEMENT_TYPES as ELEMENT_TYPES } from '../data/dashboard-template/constants';

let cachedWidgetSchema: any = null;

const schemaLayoutProperties = {
  type: 'object',
  properties: {
    fraction: { type: 'number' },
    height: { type: 'number' },
  },
  additionalProperties: false,
};

function generateWidgetSchema() {
  if (cachedWidgetSchema) return cachedWidgetSchema;

  function generateAspectBindings(chartType: TpChartManifest): any {
    const schema: any = {
      type: 'object',
      properties: {},
      required: [],
    };
    for (const aspect of chartType.aspects) {
      schema.properties[aspect.id] = { type: ['string', 'object'], description: `"${aspect.name}" table column ID` };
      if (aspect.required) schema.required.push(aspect.id);
    }
    return schema;
  }

  function generateCompoundAspectBindings(chartType: TpChartManifest): any {
    const schema: any = {
      type: 'object',
      properties: {},
      required: [],
    };
    for (const aspect of chartType.compoundAspects!) {
      schema.properties[aspect.id] = {
        type: 'array',
        items: { type: 'string' },
        description: `"${aspect.name}" table column ID list`,
      };
      if (aspect.required) schema.required.push(aspect.id);
    }
    return schema;
  }

  function generateAspectBindingsSec(chartType: TpChartManifest): any {
    const schema: any = {
      type: 'object',
      properties: {},
      required: [],
    };
    for (const aspect of chartType.secondTable!.aspects) {
      schema.properties[aspect.id] = { type: ['string', 'object'], description: `"${aspect.name}" table column ID` };
      if (aspect.required) schema.required.push(aspect.id);
    }
    return schema;
  }

  function generateConfig(chartType: TpChartManifest): any {
    const schema: any = {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: true,
    };
    schema.properties.userCanRemove = {
      type: 'boolean',
      description: 'Whether the user may remove this widget from the dashboard.',
    };
    for (const configParam of chartType.configParams) {
      let configSchema: any = {
        description: configParam.name,
      };
      if (configParam.description) configSchema.description += '. ' + configParam.description;
      switch (configParam.paramType.configParamTypeId) {
        case CONFIGPARAMTYPES.Range:
          configSchema.type = 'number';
          configSchema.minimum = configParam.paramType.minVal;
          configSchema.maximum = configParam.paramType.maxVal;
          break;
        case CONFIGPARAMTYPES.Value:
          configSchema.type = 'number';
          break;
        case CONFIGPARAMTYPES.Boolean:
          configSchema.type = 'boolean';
          break;
        case CONFIGPARAMTYPES.String:
          configSchema.type = 'string';
          break;
        case CONFIGPARAMTYPES.MultipleChoice:
          configSchema.type = 'string';
          configSchema.enum = configParam.paramType.choices.map((choice: any) => choice.id);
          break;
        case CONFIGPARAMTYPES.DiscreteColorScheme:
          configSchema.type = 'string';
          configSchema.enum = discreteColorSets.map((set) => set.name);
          break;
        case CONFIGPARAMTYPES.NumericalColorScheme:
          configSchema.type = 'string';
          configSchema.enum = numericalColorSets.map((set) => set.name);
          break;
        case CONFIGPARAMTYPES.JSON:
          if (configParam.paramType.schema) {
            configSchema = { ...configSchema, ...configParam.paramType.schema };
          }
          break;
      }
      schema.properties[configParam.id] = configSchema;
    }
    return schema;
  }

  cachedWidgetSchema = {
    anyOf: [],
  };
  for (const chartType of allChartTypeManifests) {
    const chartTypeSchema: any = {
      type: 'object',
      title: chartType.name,

      properties: {
        chartType: { type: 'string', const: chartType.id },
        title: { type: 'string', description: 'Optional custom title of the chart.' },
        description: {
          type: 'string',
          description: 'Optional description of the chart. This will be displayed as part of the chart documentation.',
        },
        selector: {
          type: 'string',
          description:
            'When defining this widget inline a section element, use selector to identify the chart for dashboard actions.',
        },
        layoutProperties: schemaLayoutProperties,

        dataBinding: {
          type: 'object',
          description:
            'Defines how the different inputs needed by the chart are bound to the data in the data context.',
          properties: {
            dataTableId: {
              type: 'string',
              description: 'Identifier of the data context table this chart should take its data from',
            },
            aspectBindings: generateAspectBindings(chartType),
          },
          required: ['dataTableId'],
        },

        config: generateConfig(chartType),
        filter: generateFilterSchema('Optional filter applied to the data shown in the chart.'),
      },
      required: ['chartType', 'layoutProperties', 'dataBinding'],
    };

    if (chartType.compoundAspects)
      chartTypeSchema.properties.dataBinding.properties.compoundAspectBindings =
        generateCompoundAspectBindings(chartType);

    if (chartType.secondTable) {
      chartTypeSchema.properties.dataBinding.properties.secondDataTableId = {
        type: 'string',
        description: 'Identifier of the secondary data context table this chart should take its data from',
      };
      chartTypeSchema.properties.dataBinding.properties.secondAspectBindings = generateAspectBindingsSec(chartType);
    }

    if (chartType.showDataMatrix) {
      chartTypeSchema.properties.dataBinding.properties.columnsCanonicalTypeId = {
        type: 'string',
        description:
          'This widget takes a data matrix, using a set of columns that are linked to another canonical type. This property specifies this canonical type.',
      };
      chartTypeSchema.properties.dataBinding.required.push('columnsCanonicalTypeId');
      chartTypeSchema.properties.dataBinding.properties.columnsGroupId = {
        type: 'string',
        description: 'Group identifier of the columns that are used in the data matrix.',
      };
      chartTypeSchema.properties.dataBinding.required.push('columnsGroupId');
    }

    cachedWidgetSchema.anyOf.push(chartTypeSchema);
  }

  return cachedWidgetSchema;
}

// const elemList = { type: 'array', "items": elementTypeRef }; //<-- NOTE: this would allow nested schemas via recursion, but it seems to crash Ajv due to a stack overflow
const elemList = {
  type: 'array',
  description: 'List of child elements to be displayed. Should be of the type described in elements/sections/elements.',
};

const schemaLayout = {
  type: 'object',
  title: 'Layout',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.Layout },
    align: { type: 'string', enum: ['horizontal', 'vertical', 'sequence'] },
    layoutProperties: schemaLayoutProperties,
    elements: elemList,
  },
  required: ['type', 'align', 'elements'],
  additionalProperties: false,
};

const schemaLayoutVertical = {
  type: 'object',
  title: 'Vertical layout',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.LayoutVertical },
    layoutProperties: schemaLayoutProperties,
    elements: elemList,
  },
  required: ['type', 'elements'],
  additionalProperties: false,
};

const schemaLayoutHorizontal = {
  type: 'object',
  title: 'Horizontal layout',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.LayoutHorizontal },
    layoutProperties: schemaLayoutProperties,
    elements: elemList,
  },
  required: ['type', 'elements'],
  additionalProperties: false,
};

const schemaLayoutSequence = {
  type: 'object',
  title: 'Sequence layout',
  description:
    'Layout that wraps elements horizontally into sequence rows. Warning: this layout should only be used as a direct child of a section.',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.LayoutSequence },
    layoutProperties: schemaLayoutProperties,
    elements: elemList,
  },
  required: ['type', 'elements'],
  additionalProperties: false,
};

const schemaTabber = {
  type: 'object',
  title: 'Tabber layout',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.Tabber },
    layoutProperties: schemaLayoutProperties,
    titles: { type: 'array', items: { type: 'string' } },
    elements: elemList,
  },
  required: ['type', 'titles', 'elements'],
  additionalProperties: false,
};

const schemaText = {
  type: 'object',
  title: 'Text',
  description: 'Defines a text snippet',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.Text },
    content: { type: 'string', description: 'The actual text content to be displayed' },
    layoutProperties: schemaLayoutProperties,
  },
  required: ['type', 'content'],
  additionalProperties: false,
};

const schemaBitmap = {
  type: 'object',
  title: 'Bitmap',
  description: 'Displays a bitmap that is part of the data context',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.Bitmap },
    bitmapId: { type: 'string' },
    description: {
      type: 'string',
      description:
        'Optional description of the bitmap displayed on the documentation page (defaults to description in image manifest).',
    },
    layoutProperties: schemaLayoutProperties,
  },
  required: ['type', 'bitmapId'],
  additionalProperties: false,
};

const pairwizeComparisonGallery = {
  type: 'object',
  title: 'Pairwize comparison gallery',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.PairwizeComparisonGallery },
    maxCount: { type: 'integer' },
    dataTableId: { type: 'string' },
    propertyGroupId: { type: 'string' },
    variable1: { type: 'string' },
    variable2: { type: 'string' },
    element: { type: ['string', 'object'] },
  },
  required: ['type', 'dataTableId', 'propertyGroupId', 'variable1', 'variable2', 'element'],
  additionalProperties: false,
};

const schemaGallery = {
  type: 'object',
  title: 'Gallery',
  description: 'Iterates over a group of properties, and displays an element for each property in that group.',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.Gallery },
    columnCount: { type: 'integer', description: 'Number of gallery elements to be displayed on a single row.' },
    maxCount: {
      type: 'integer',
      description:
        'Maximum number of gallery elements to be displayed simultaneously. If more elements are present, they will be split over several tabs.',
    },
    dataTableId: { type: 'string' },
    propertyGroupId: { type: 'string', description: 'Defines the set of properties to iterate over.' },
    variable: {
      type: 'string',
      description:
        'Name of the variable that contains the curently used property. This name can be used in the configuration of the iterated element by prefixing it with $.',
    },
    element: { type: ['string', 'object'], description: 'element to be displayed for each property.' },
  },
  required: ['type', 'columnCount', 'dataTableId', 'propertyGroupId', 'variable', 'element'],
  additionalProperties: false,
};

const schemaCategoricalGallery = {
  type: 'object',
  title: 'Categories Gallery',
  description:
    'Iterates over all states of a categorical property, and for each state creates an element displaying all items belonging to that state. Note: if columnCount and maxCount are both set to 1, each tab contains a single categorical state, and that state is used as a tab header.',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.CategoriesGallery },
    columnCount: { type: 'integer', description: 'Number of gallery elements to be displayed on a single row.' },
    maxCount: {
      type: 'integer',
      description:
        'Maximum number of gallery elements to be displayed simultaneously. If more elements are present, they will be split over several tabs.',
    },
    dataTableId: { type: 'string' },
    property: { type: 'string', description: 'Identifier of the property containing the categorical data.' },
    variable: {
      type: 'string',
      description:
        'Name of the variable that contains the curently used property. This name can be used in the configuration of the iterated element by prefixing it with $.',
    },
    sortBy: {
      type: 'string',
      enum: categoricalOrderValues,
      description: 'Order in which the categories will be added (the default value is count).',
    },
    element: { type: ['string', 'object'], description: 'element to be displayed for each property.' },
  },
  required: ['type', 'columnCount', 'dataTableId', 'property', 'variable', 'element'],
  additionalProperties: false,
};

const schemaActionButtons = {
  type: 'object',
  title: 'Action Buttons',
  description: 'Defines a set of action buttons',
  properties: {
    type: { type: 'string', const: ELEMENT_TYPES.ActionButtons },
    actions: {
      type: 'array',
      items: {
        type: 'string',
        description:
          'Identifier of an action, corresponding to an item in the /elements/actions part of the yaml definition.',
      },
    },
  },
  required: ['type', 'actions'],
  additionalProperties: false,
};

const schemaSectionElement = {
  anyOf: [
    {
      type: 'string',
      title: 'Widget reference',
      description: 'Contains the identifier of a widget, as defined in the elements/widget section of the template',
    }, // reference to id of an actual chart
    {
      type: 'object',
      title: 'Widget definition',
      description:
        'Contains the full definition of a widget, similar to the elements/widget section of the template. See the widget documentation for details on the available properties.',
      properties: {
        type: { type: 'string', const: ELEMENT_TYPES.Widget },
      },
      required: ['type'],
      additionalProperties: true,
    },
    schemaText,
    schemaLayout,
    schemaLayoutVertical,
    schemaLayoutHorizontal,
    schemaLayoutSequence,
    schemaTabber,
    schemaBitmap,
    pairwizeComparisonGallery,
    schemaGallery,
    schemaCategoricalGallery,
    schemaActionButtons,
    // elementTypeRef, //<-- NOTE: this would allow nested schemas via recursion, but it seems to crash Ajv on a stack overflow
  ],
};

const schemaSectionItem = {
  type: 'object',
  properties: {
    id: { type: 'string', description: 'Unique identifier' },
    title: { type: 'string', description: 'Section title' },
    description: {
      type: 'string',
      description:
        'Optional section description that will be accessible from a documentation page. This string can contain markdown syntax.',
    },
    requiredTableId: {
      type: 'string',
      description: 'Table ID that must be present before this section will be rendered',
    },
    collapsed: { type: 'boolean', description: 'If true, the section is shown collapsed by default' },
    elements: {
      type: 'array',
      items: schemaSectionElement,
      description: 'List of elements in the section. The elements will be dispayed under each other.',
    },
  },
  required: ['id'],
  additionalProperties: false,
};

const schemaActionItem = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    icon: { type: 'string' },
    steps: {
      type: 'array',
      description: 'Sequence of steps to be performed when the action is executed.',
      items: { anyOf: stepDefinitionsList.map((stepDefinition) => stepDefinition.getJsonSchema()) },
    },
    triggers: {
      type: 'array',
      description: 'Definition of one or more events that trigger the execution of this action',
      items: { anyOf: dashboardActionTriggerDefs.map((triggerDefinition) => triggerDefinition.getJsonSchema()) },
    },
  },
  required: ['name'],
};

const schemaTopbarItem = {
  type: 'object',
  properties: {
    id: { type: 'string', description: 'Name of the entity type to include.' },
  },
  required: ['id'],
};

const schemaCategoricalOrderValue = {
  type: 'object',
  description: 'Defines for which property the categorical order is defined, and what the actual order is.',
  properties: {
    canonicalTypeId: { type: 'string', description: 'Identifier of the canonical type containing the property.' },
    propertyId: { type: 'string', description: 'Identifier of the property for which the order is defined.' },
    order: {
      type: 'array',
      description: 'List of categorical values in the desired order.',
      items: { type: 'string' },
    },
  },
  required: ['canonicalTypeId', 'propertyId', 'order'],
};

const schemaWidgetConfigSettings = {
  colorMapping: {
    type: 'object',
    description: 'Optional settings related to the color mapping of the chart.',
    properties: {
      customCategoricalOrders: {
        type: 'array',
        description:
          'Optional preset order for categorical values, which can be used to ensure a consistent color mapping across different charts.',
        items: schemaCategoricalOrderValue,
      },
    },
  },
};

const schemaDashboardTemplate = {
  type: 'object',
  properties: {
    displayName: { type: 'string', description: 'Name of the template' },
    assetType: { type: 'string', description: 'Identifier of the asset type this dashboard applies to' },
    templateId: { type: 'string', description: 'Identifier of this template' },
    version: { type: 'string' },
    elements: {
      type: 'object',
      properties: {
        widgets: {
          type: 'object',
          description: 'Set of chart widgets used in the dashboard.',
          additionalProperties: generateWidgetSchema(),
        },
        sections: { type: 'array', description: 'List of sections in the dashboard', items: schemaSectionItem },
        actions: {
          type: 'object',
          description:
            'Optional. Set of actions defined for the dashboard. Actions can be positioned in the dashboard via elements of the type "actionButtons".',
          additionalProperties: schemaActionItem,
        },
        topbar: {
          type: 'array',
          description: 'Optional: Add entityTypes.id to customise the filtering selection in navigation bar.',
          items: schemaTopbarItem,
        },
        widgetGlobalConfig: {
          type: 'object',
          description: 'Optional global settings for widget configurations.',
          properties: schemaWidgetConfigSettings,
        },
      },
      required: ['widgets', 'sections'],
    },
  },
  required: ['elements'],
  additionalProperties: false,

  // NOTE: the code below would be used in case we use the $def feature of Ajv. Kept in here for future reference
  // $defs: {
  //   element: schemaSectionElement,
  // },
};

export default schemaDashboardTemplate;
